import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Chip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getMasters,
  createMaster,
  updateMaster,
  deleteMaster,
} from '../store/slices/questsSlice';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MasterFormDialog from '../components/masters/MasterFormDialog';

const getMasterName = (m) =>
  [m.lastName, m.firstName, m.patronymic].filter(Boolean).join(' ') || m.name || m.login || '—';

const MastersPage = () => {
  const dispatch = useDispatch();
  const { masters, loading } = useSelector((state) => state.quests);

  const [formOpen, setFormOpen] = useState(false);
  const [editingMaster, setEditingMaster] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    dispatch(getMasters());
  }, [dispatch]);

  const showNotification = (message, severity = 'info') =>
    setNotification({ open: true, message, severity });

  const handleCreate = () => {
    setEditingMaster(null);
    setFormOpen(true);
  };

  const handleEdit = (master) => {
    setEditingMaster(master);
    setFormOpen(true);
  };

  const handleDelete = (master) => {
    if (!window.confirm(`Удалить исполнителя "${getMasterName(master)}"?`)) return;
    dispatch(deleteMaster(master.id))
      .unwrap()
      .then(() => showNotification('Исполнитель удалён', 'success'))
      .catch((err) => showNotification(
        typeof err === 'string' && err ? err : 'Ошибка удаления',
        'error'
      ));
  };

  const handleSave = async (data) => {
    try {
      if (editingMaster) {
        await dispatch(updateMaster({ masterId: editingMaster.id, masterData: data })).unwrap();
        showNotification('Данные исполнителя обновлены', 'success');
      } else {
        await dispatch(createMaster(data)).unwrap();
        showNotification('Исполнитель создан', 'success');
      }
      setFormOpen(false);
      setEditingMaster(null);
    } catch (err) {
      showNotification(err || 'Ошибка сохранения', 'error');
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 132px)', display: 'flex', flexDirection: 'column', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 18 }}>М</Typography>
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Исполнители
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Управление исполнителями ({masters?.length || 0})
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          Новый исполнитель
        </Button>
      </Box>

      {/* Table */}
      {loading && !masters?.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', flex: 1 }}>
          <TableContainer sx={{ height: '100%' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#fafafa' }}>ФИО</TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#fafafa' }}>Телефон</TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#fafafa', width: 120 }} align="center">
                    Статус
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#fafafa', width: 100 }} align="center">
                    Действия
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {masters?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">Нет исполнителей</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  masters?.map((master) => (
                    <TableRow
                      key={master.id}
                      hover
                      sx={{ '&:last-child td': { border: 0 } }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {getMasterName(master)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {master.phone || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={master.isActive ? 'Активен' : 'Неактивен'}
                          size="small"
                          color={master.isActive ? 'success' : 'default'}
                          variant={master.isActive ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Редактировать">
                          <IconButton size="small" onClick={() => handleEdit(master)} sx={{ color: 'primary.main' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                          <IconButton size="small" onClick={() => handleDelete(master)} sx={{ color: 'error.main' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Form dialog */}
      <MasterFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingMaster(null); }}
        onSave={handleSave}
        master={editingMaster}
      />

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.severity === 'error' ? 10000 : 4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: 120 }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%', maxWidth: 560 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MastersPage;
